import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import ClipboardCheckIcon from "@src/lib/icons/18px/clipboard-check";
import EyeOutlineIcon from "@src/lib/icons/18px/eye-outline";
import GearOutlineIcon from "@src/lib/icons/18px/gear-outline";
import RulerPenOutlineIcon from "@src/lib/icons/18px/ruler-pen-outline";
import type { Option } from "@src/lib/ui/toggleOption";
import { LibraryItemDetailPage_LibraryItemFragment } from "../LibraryItemDetailPageContainer.generated";
import useLibraryItemPermissions from "../hooks/useLibraryItemPermissions";
import {
  NumberParam,
  StringParam,
  createEnumParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { getCourseHomeScreenId } from "../course/hooks/useCourseScreensAtom";
import { LibraryItemType } from "@src/types.generated";

/** Which slice of Reporting → People is showing (redesigned page only). */
export type AudienceFacet = "assigned" | "past-due" | "library-access";
export const AUDIENCE_FACETS: AudienceFacet[] = [
  "assigned",
  "past-due",
  "library-access",
];

export interface LibraryItemQueryParamsContextState {
  manageTabs: Tab[];
  mainTabs: Option<string>[];
  reportingTabs: Tab[];
  selectedMainTab: string;
  selectedManageTab: string;
  selectedReportingTab: string;
  selectedPromptTab: PromptTab;
  selectedPromptId: string;
  selectedCourseReportingPromptId: string | undefined;
  selectedCourseReportingCourseVersionId: number | undefined;
  selectedSkillReportingStepId: string | undefined;
  selectedSkillReportingSkillVersionId: string | undefined;
  selectedModuleReportingModuleVersionId: string | undefined;
  selectedAudienceFacet: AudienceFacet;
  setAudienceFacet: (facet: AudienceFacet) => void;
  setMainTab: (value: string) => void;
  setManageTab: (value: string) => void;
  setReportingTab: (value: string) => void;
  setPromptTab: (newTab: PromptTab, promptId?: string) => void;
  setPromptId: (newPromptId: string, promptTab: PromptTab) => void;
  setSelectedCourseReportingPromptId: (promptId: string | undefined) => void;
  setSelectedCourseReportingCourseVersionId: (courseVersionId: number) => void;
  setSelectedSkillReportingStepId: (stepId: string | undefined) => void;
  setSelectedSkillReportingSkillVersionId: (skillVersionId: string) => void;
  setSelectedModuleReportingModuleVersionId: (moduleVersionId: string) => void;
}

type Props = {
  children: ReactNode;
  libraryItem?: LibraryItemDetailPage_LibraryItemFragment | null;
};

export const LibraryItemQueryParamsContext = createContext<
  LibraryItemQueryParamsContextState | undefined
>(undefined);

export const useLibraryItemQueryParams =
  (): LibraryItemQueryParamsContextState => {
    const context = useContext(LibraryItemQueryParamsContext);
    if (!context) {
      throw new Error(
        "useLibraryItemQueryParams must be used within a LibraryItemQueryParamsContextProvider",
      );
    }
    return context;
  };

// The previous page has no audience facets; the redesigned provider
// (LibraryItemQueryParamsContextV2) owns them.
const noopSetAudienceFacet = (): void => undefined;

export const LibraryItemQueryParamsContextProvider: React.FC<Props> = (
  props,
) => {
  const libraryItemPermissions = useLibraryItemPermissions(props?.libraryItem);
  const mainTabs = useMemo(() => {
    if (props.libraryItem?.type === LibraryItemType.ScormCourse) {
      return [];
    }
    return MAIN_TABS.filter((tab) => {
      return libraryItemPermissions?.hasEditContentPermission
        ? tab.value !== "preview"
        : tab.value !== "build";
    });
  }, [
    libraryItemPermissions?.hasEditContentPermission,
    props.libraryItem?.type,
  ]);
  const mainTabValues = useMemo(() => mainTabs.map((t) => t.value), [mainTabs]);
  const manageTabs = useMemo(() => {
    if (props.libraryItem?.type === LibraryItemType.Module) {
      return MODULE_MANAGE_TABS;
    } else if (props.libraryItem?.type === LibraryItemType.Course) {
      return COURSE_MANAGE_TABS;
    } else if (props.libraryItem?.type === LibraryItemType.ScormCourse) {
      return props.libraryItem.scormCourse?.publishedVersion
        ? SCORM_COURSE_MANAGE_TABS
        : SCORM_UNPUBLISHED_MANAGE_TABS;
    } else if (props.libraryItem?.type === LibraryItemType.Skill) {
      return CHECK_IN_TABS;
    } else if (props.libraryItem?.type === LibraryItemType.Task) {
      return TASK_TABS;
    } else if (props.libraryItem?.type === LibraryItemType.Audit) {
      return AUDIT_TABS;
    } else if (props.libraryItem?.type === LibraryItemType.TrainingResource) {
      return TRAINING_RESOURCE_TABS;
    }
    return MODULE_MANAGE_TABS;
  }, [
    props.libraryItem?.type,
    props.libraryItem?.scormCourse?.publishedVersion,
  ]);
  const manageTabValues = useMemo(() => {
    return manageTabs.map((t) => t.value);
  }, [manageTabs]);
  const reportingTabs = useMemo(() => {
    if (props.libraryItem?.type === LibraryItemType.Course) {
      return COURSE_REPORTING_TABS;
    } else {
      return MODULE_REPORTING_TABS;
    }
  }, [props.libraryItem?.type]);
  const reportingTabValues = useMemo(() => {
    return reportingTabs.map((t) => t.value);
  }, [reportingTabs]);
  const defaultPromptTab = useMemo(() => {
    if (
      libraryItemPermissions?.hasEditContentPermission ||
      !libraryItemPermissions?.hasCommentPermission
    ) {
      return PromptTab.Design;
    }
    return PromptTab.Comments;
  }, [
    libraryItemPermissions?.hasEditContentPermission,
    libraryItemPermissions?.hasCommentPermission,
  ]);
  const [queryParams, setQueryParams] = useQueryParams({
    mainTab: withDefault(createEnumParam(mainTabValues), mainTabValues[0]),
    manageTab: withDefault(
      createEnumParam(manageTabValues),
      manageTabValues[0],
    ),

    reportingTab: withDefault(
      createEnumParam(reportingTabValues),
      reportingTabValues[0],
    ),
    "prompt-tab": withDefault(
      createEnumParam(Object.values(PromptTab)),
      defaultPromptTab,
    ),
    screenId: withDefault(StringParam, getCourseHomeScreenId()),
    selectedCourseReportingPromptId: withDefault(StringParam, undefined),
    selectedCourseReportingCourseVersionId: withDefault(NumberParam, undefined),
    selectedSkillReportingStepId: withDefault(StringParam, undefined),
    selectedSkillReportingSkillVersionId: withDefault(StringParam, undefined),
    selectedModuleReportingModuleVersionId: withDefault(StringParam, undefined),
  });
  const setMainTab = useCallback(
    (newTab: string) => {
      setQueryParams({ mainTab: newTab });
    },
    [setQueryParams],
  );
  const setManageTab = useCallback(
    (newTab: string) => {
      setQueryParams({ mainTab: "manage", manageTab: newTab });
    },
    [setQueryParams],
  );
  const setPromptTab = useCallback(
    (newPromptTab: PromptTab, newPromptId?: string) => {
      setQueryParams(
        {
          "prompt-tab": newPromptTab,
          screenId: newPromptId || queryParams.screenId,
        },
        "replaceIn",
      );
    },
    [queryParams.screenId, setQueryParams],
  );
  const setReportingTab = useCallback(
    (newTab: string) => {
      setQueryParams({ mainTab: "reporting", reportingTab: newTab });
    },
    [setQueryParams],
  );
  const setSelectedCourseReportingPromptId = useCallback(
    (newPromptId: string | undefined) => {
      setQueryParams({ selectedCourseReportingPromptId: newPromptId });
    },
    [setQueryParams],
  );
  const setSelectedCourseReportingCourseVersionId = useCallback(
    (newId: number) => {
      setQueryParams({ selectedCourseReportingCourseVersionId: newId });
    },
    [setQueryParams],
  );
  const setSelectedSkillReportingStepId = useCallback(
    (newId: string | undefined) => {
      setQueryParams({ selectedSkillReportingStepId: newId });
    },
    [setQueryParams],
  );
  const setSelectedSkillReportingSkillVersionId = useCallback(
    (newId: string) => {
      setQueryParams({ selectedSkillReportingSkillVersionId: newId });
    },
    [setQueryParams],
  );
  const setSelectedModuleReportingModuleVersionId = useCallback(
    (newId: string) => {
      setQueryParams({ selectedModuleReportingModuleVersionId: newId });
    },
    [setQueryParams],
  );
  const setPromptId = useCallback(
    (newPromptId: string, newPromptTab: PromptTab) => {
      setQueryParams(
        {
          screenId: newPromptId,
          "prompt-tab": newPromptTab,
        },
        "replaceIn",
      );
    },
    [setQueryParams],
  );
  const contextState: LibraryItemQueryParamsContextState = useMemo(() => {
    return {
      mainTabs,
      manageTabs,
      reportingTabs,
      setMainTab,
      setManageTab,
      setPromptTab,
      setPromptId,
      setReportingTab,
      setSelectedCourseReportingPromptId,
      setSelectedCourseReportingCourseVersionId,
      setSelectedSkillReportingStepId,
      setSelectedSkillReportingSkillVersionId,
      setSelectedModuleReportingModuleVersionId,
      selectedMainTab: queryParams.mainTab,
      selectedManageTab: queryParams.manageTab,
      selectedPromptTab: queryParams["prompt-tab"],
      selectedPromptId: queryParams.screenId,
      selectedReportingTab: queryParams.reportingTab,
      selectedCourseReportingPromptId:
        queryParams.selectedCourseReportingPromptId,
      selectedCourseReportingCourseVersionId:
        queryParams.selectedCourseReportingCourseVersionId,
      selectedSkillReportingStepId: queryParams.selectedSkillReportingStepId,
      selectedSkillReportingSkillVersionId:
        queryParams.selectedSkillReportingSkillVersionId,
      selectedModuleReportingModuleVersionId:
        queryParams.selectedModuleReportingModuleVersionId,
      selectedAudienceFacet: "assigned",
      setAudienceFacet: noopSetAudienceFacet,
    };
  }, [
    mainTabs,
    manageTabs,
    reportingTabs,
    setMainTab,
    setManageTab,
    setPromptTab,
    setPromptId,
    setReportingTab,
    setSelectedCourseReportingPromptId,
    setSelectedCourseReportingCourseVersionId,
    setSelectedSkillReportingStepId,
    setSelectedSkillReportingSkillVersionId,
    setSelectedModuleReportingModuleVersionId,
    queryParams,
  ]);
  return (
    <LibraryItemQueryParamsContext.Provider value={contextState}>
      {props.children}
    </LibraryItemQueryParamsContext.Provider>
  );
};

export type Tab = {
  label: string;
  value: string;
  /** A test id for the tab's button; `tab-<value>` when unset. */
  testId?: string;
};
export const MAIN_TABS: Option<string>[] = [
  {
    label: "Preview",
    value: "preview",
    icon: <EyeOutlineIcon className="size-4" />,
  },
  {
    label: "Build",
    value: "build",
    icon: <RulerPenOutlineIcon className="size-4" />,
  },
  {
    label: "Manage",
    value: "manage",
    icon: <GearOutlineIcon className="size-4" />,
  },
  {
    label: "Reporting",
    value: "reporting",
    icon: <ClipboardCheckIcon className="size-4" />,
  },
];
export const MODULE_MANAGE_TABS: Tab[] = [
  { label: "Assignments", value: "assignments" },
  { label: "Library Access", value: "library-access" },
  { label: "Settings", value: "settings" },
];
export const COURSE_MANAGE_TABS: Tab[] = [
  { label: "Assignments", value: "assignments" },
  { label: "Library Access", value: "library-access" },
  { label: "Settings", value: "settings" },
];
export const TRAINING_RESOURCE_TABS: Tab[] = [
  { label: "Library Access", value: "library-access" },
  { label: "Settings", value: "settings" },
];
export const CHECK_IN_TABS: Tab[] = [
  { label: "Assignments", value: "assignments" },
  { label: "Settings", value: "settings" },
];
export const TASK_TABS: Tab[] = [
  { label: "Library Access", value: "library-access" },
  { label: "Schedule", value: "schedule" },
  { label: "Settings", value: "settings" },
];
export const AUDIT_TABS: Tab[] = [
  { label: "Library Access", value: "library-access" },
  { label: "Schedule", value: "schedule" },
  { label: "Settings", value: "settings" },
];
export const MODULE_REPORTING_TABS: Tab[] = [
  { label: "Responses", value: "responses" },
];
export const COURSE_REPORTING_TABS: Tab[] = [
  { label: "Overview", value: "overview" },
  { label: "Reviews", value: "reviews" },
];

export const SCORM_COURSE_MANAGE_TABS: Tab[] = [
  { label: "Settings", value: "settings" },
  { label: "Assignments", value: "assignments" },
  { label: "Library Access", value: "library-access" },
];

export const SCORM_UNPUBLISHED_MANAGE_TABS: Tab[] = [
  { label: "Settings", value: "settings" },
];

export enum PromptTab {
  Design = "design",
  Comments = "comments",
}
